import {
  action,
  atom,
  onUpdate,
  type Ctx,
  type CtxSpy,
} from "@reatom/framework";
import { urlAtom } from "@reatom/url";
import {
  PriceDtoCurrencyEnum,
  ProductControllerSearchV2GendersEnum,
  ProductControllerSearchV2SourcesEnum,
} from "definitions/product-storage";
import { RoutesPaths } from "src/routes/RoutesPaths";

import {
  formatCreatedDate,
  parseBoolean,
  parseCreatedDate,
  parseEnumArray,
  parseIdArray,
  parseString,
} from "../../utils";
import { ProductFilters } from "../../widgets/ProductFilters/ProductFilters";

const LIST_SEPARATOR = "\u0000";

const CURRENCY_VALUES = Object.values(PriceDtoCurrencyEnum);
const GENDER_VALUES = Object.values(ProductControllerSearchV2GendersEnum);
const SOURCE_VALUES = Object.values(ProductControllerSearchV2SourcesEnum);

const FILTER_PARAM_KEYS = [
  "search",
  "currency",
  "brandIds",
  "categoryIds",
  "priceFrom",
  "priceTo",
  "createdFrom",
  "createdTo",
  "viewAllSelected",
  "includeCustom",
  "genders",
  "sources",
  "excludeSamples",
];

const readParam = (
  ctx: CtxSpy,
  previous: string,
  read: (url: URL) => string,
) => {
  const url = ctx.spy(urlAtom);

  return url.pathname === RoutesPaths.LK.GOODS ? read(url) : previous;
};

const rawParam = (key: string) =>
  atom(
    (ctx, previous = "") =>
      readParam(ctx, previous, (url) => url.searchParams.get(key) ?? ""),
    `${key}Param`,
  );

const rawListParam = (key: string) =>
  atom(
    (ctx, previous = "") =>
      readParam(ctx, previous, (url) =>
        url.searchParams.getAll(key).join(LIST_SEPARATOR),
      ),
    `${key}Param`,
  );

const splitList = (raw: string) => (raw ? raw.split(LIST_SEPARATOR) : []);

const parseNumber = (raw: string) => {
  const parsed = Number(raw);

  return raw !== "" && !Number.isNaN(parsed) ? parsed : undefined;
};

const searchParam = rawParam("search");
const currencyParam = rawParam("currency");
const priceFromParam = rawParam("priceFrom");
const priceToParam = rawParam("priceTo");
const createdFromParam = rawParam("createdFrom");
const createdToParam = rawParam("createdTo");
const viewAllSelectedParam = rawParam("viewAllSelected");
const includeCustomParam = rawParam("includeCustom");
const brandIdsParam = rawListParam("brandIds");
const categoryIdsParam = rawListParam("categoryIds");
const gendersParam = rawListParam("genders");
const sourcesParam = rawListParam("sources");
const excludeSamplesParam = rawListParam("excludeSamples");
const selectionIdParam = rawParam("selectionId");

const currencyFilter = atom((ctx) => {
  const raw = ctx.spy(currencyParam);

  return CURRENCY_VALUES.includes(raw as PriceDtoCurrencyEnum)
    ? (raw as PriceDtoCurrencyEnum)
    : PriceDtoCurrencyEnum.Rub;
}, "currencyFilter");

const brandIdsFilter = atom(
  (ctx) => parseIdArray(splitList(ctx.spy(brandIdsParam))),
  "brandIdsFilter",
);

const categoryIdsFilter = atom(
  (ctx) => parseIdArray(splitList(ctx.spy(categoryIdsParam))),
  "categoryIdsFilter",
);

const gendersFilter = atom(
  (ctx) => parseEnumArray(splitList(ctx.spy(gendersParam)), GENDER_VALUES),
  "gendersFilter",
);

const sourcesFilter = atom(
  (ctx) => parseEnumArray(splitList(ctx.spy(sourcesParam)), SOURCE_VALUES),
  "sourcesFilter",
);

const excludeSamplesFilter = atom(
  (ctx) => parseIdArray(splitList(ctx.spy(excludeSamplesParam))),
  "excludeSamplesFilter",
);

export const selectionIdAtom = atom(
  (ctx) => parseIdArray([ctx.spy(selectionIdParam)])?.[0],
  "selectionIdAtom",
);

export const filterAtom = atom<ProductFilters>(
  (ctx) => ({
    search: parseString(ctx.spy(searchParam)),
    currency: ctx.spy(currencyFilter),
    brandIds: ctx.spy(brandIdsFilter),
    categoryIds: ctx.spy(categoryIdsFilter),
    priceFrom: parseNumber(ctx.spy(priceFromParam)),
    priceTo: parseNumber(ctx.spy(priceToParam)),
    createdFrom: parseCreatedDate(ctx.spy(createdFromParam)),
    createdTo: parseCreatedDate(ctx.spy(createdToParam)),
    viewAllSelected: parseBoolean(ctx.spy(viewAllSelectedParam) || undefined),
    includeCustom: parseBoolean(ctx.spy(includeCustomParam) || undefined),
    genders: ctx.spy(gendersFilter),
    sources: ctx.spy(sourcesFilter),
    excludeSamples: ctx.spy(excludeSamplesFilter),
  }),
  "filterAtom",
);

export const onFiltersChange = (cb: (ctx: Ctx) => void) => {
  const observedFilters = atom<ProductFilters | null>(null);

  return onUpdate(filterAtom, (ctx, filters) => {
    const previousFilters = ctx.get(observedFilters);

    observedFilters(ctx, filters);

    if (previousFilters !== null && previousFilters !== filters) {
      cb(ctx);
    }
  });
};

const writeFilters = (url: URL, filters: ProductFilters): URL => {
  const next = new URL(url.href);

  FILTER_PARAM_KEYS.forEach((key) => next.searchParams.delete(key));

  const setValue = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== "") {
      next.searchParams.set(key, String(value));
    }
  };

  const setList = (key: string, values?: (string | number)[]) => {
    values?.forEach((value) => next.searchParams.append(key, String(value)));
  };

  setValue("search", filters.search);
  setValue(
    "currency",
    filters.currency === PriceDtoCurrencyEnum.Rub
      ? undefined
      : filters.currency,
  );
  setValue("priceFrom", filters.priceFrom);
  setValue("priceTo", filters.priceTo);
  setValue("createdFrom", formatCreatedDate(filters.createdFrom));
  setValue("createdTo", formatCreatedDate(filters.createdTo));
  setValue("viewAllSelected", filters.viewAllSelected ? "true" : undefined);
  setValue(
    "includeCustom",
    filters.includeCustom === undefined
      ? undefined
      : String(filters.includeCustom),
  );
  setList("brandIds", filters.brandIds);
  setList("categoryIds", filters.categoryIds);
  setList("genders", filters.genders);
  setList("sources", filters.sources);
  setList("excludeSamples", filters.excludeSamples);

  return next;
};

const writeSelectionId = (url: URL, selectionId?: number): URL => {
  const next = new URL(url.href);

  if (selectionId === undefined) {
    next.searchParams.delete("selectionId");
    next.searchParams.delete("viewAllSelected");
  } else {
    next.searchParams.set("selectionId", String(selectionId));
  }

  return next;
};

const updateUrl = (ctx: Ctx, write: (url: URL) => URL, replace: boolean) => {
  urlAtom(
    ctx,
    (url) => {
      const nextUrl = write(url);

      return nextUrl.href === url.href ? url : nextUrl;
    },
    replace,
  );
};

export const updateFilters = action(
  (ctx, patch: Partial<ProductFilters>, replace = false) => {
    const nextFilters = { ...ctx.get(filterAtom), ...patch };

    updateUrl(ctx, (url) => writeFilters(url, nextFilters), replace);
  },
  "updateFilters",
);

export const updateSelectionId = action(
  (ctx, selectionId: number | undefined, replace = false) => {
    updateUrl(ctx, (url) => writeSelectionId(url, selectionId), replace);
  },
  "updateSelectionId",
);
